import type { SelectLocationFull } from '@workspace/db/extra'
import { createContext, type ReactNode, useContext } from 'react'
import type { ApiTokenView } from './admin/use-admin-api-tokens'
import type { AdminLocationsConfig } from './admin/use-admin-locations'
import type { PermissionMatrixData } from './admin/use-admin-permissions'
import type { AdminRole } from './admin/use-admin-roles'
import type { TenantSummary } from './admin/use-admin-tenants'
import type { AdminUser } from './admin/use-admin-users'
import type { VendorSettings } from './admin/vendors'
import type { DashboardNetwork, DashboardPos, NetstatusData } from './dashboard/use-dashboard'
import type { LocationClients } from './locations/use-clients'
import type { LocationRow } from './locations/use-locations'
import type { ProvisioningState } from './locations/use-managed-devices'
import type { NetworkOverview } from './locations/use-network-overview'
import type { CashDrawerConfig, PrinterConfig, TerminalSearchHit } from './locations/use-pos-terminals'
import type { SwitchPortsData } from './locations/use-switch-ports'
import type { WanSettingsData } from './locations/use-wan-settings'

/**
 * Mock datasets for the public /preview route (no auth, no BFF).
 *
 * HOW TO EXTEND for a new screen's data hook:
 *   1. Add a field to `PreviewData` and a realistic fixture to `PREVIEW_DATA` below.
 *   2. In the hook, call `const preview = usePreviewData()` (unconditionally), then pass
 *      `enabled: !preview` and, when `preview` is set, `initialData: preview.<field>` plus
 *      `staleTime: Number.POSITIVE_INFINITY` (see `useLocations`). Give it a distinct
 *      `['preview', ...]` queryKey. The hook keeps its return shape; screens are unchanged.
 * Outside /preview the context is null and hooks take the real, authed path.
 */
export interface PreviewData {
  locations: LocationRow[]
  locationDetail: SelectLocationFull
  networkOverview: NetworkOverview
  wanSettings: WanSettingsData
  provisioningState: ProvisioningState
  switchPorts: SwitchPortsData
  clients: LocationClients
  terminalSearch: TerminalSearchHit[]
  printerConfig: PrinterConfig
  cashDrawerConfig: CashDrawerConfig
  adminUsers: AdminUser[]
  adminRoles: AdminRole[]
  adminPermissions: PermissionMatrixData
  adminTenants: TenantSummary[]
  adminTenantDetail: VendorSettings
  adminApiTokens: ApiTokenView[]
  adminCredentials: VendorSettings
  adminLocationsConfig: AdminLocationsConfig
  dashboardNetstatus: NetstatusData
  dashboardNetwork: DashboardNetwork
  dashboardPos: DashboardPos
}

const names: [string, string, boolean][] = [
  ['0005', 'Mondawmin Mall', true],
  ['0007', 'Security Square', false],
  ['0012', 'Towson Town Center', true],
  ['0019', 'Eastpoint Mall', true],
  ['0024', 'White Marsh', true],
  ['0031', 'Columbia Mall', true],
  ['0046', 'Annapolis Harbour', true],
  ['0058', 'Georgetown Park', false],
  ['0063', 'Pentagon City', true],
  ['0077', 'Tysons Corner', true],
  ['0089', 'Penn Station', true],
  ['0094', 'Wilmington Riverfront', false],
  ['0101', 'Gallery Place', true],
  ['0115', 'Fashion Outlets Philly', true],
  ['0132', 'Atlanta Lenox Square', true],
  ['0148', 'Charlotte SouthPark', true],
  ['0156', 'Richmond Short Pump', false],
  ['0163', 'Norfolk MacArthur Center', true],
  ['0177', 'Newark Broad Street', true],
  ['0184', 'Brooklyn Fulton Mall', true],
]

// Partial fixture cast: SelectLocationFull has ~40 nullable columns the mock screens never read.
const PREVIEW_LOCATION = {
  id: '00000000-0000-4000-8000-000000000005',
  code: '0005',
  aptosStoreCode: '0005',
  banner: 'dtlr',
  name: 'Mondawmin Mall',
  shortName: null,
  status: true,
  district: 'D12',
  region: 'R03',
  addressLine1: '2401 Liberty Heights Ave',
  addressCity: 'Baltimore',
  addressState: 'MD',
  addressZipCode: '21215',
  locationTypeLabel: 'Store',
  org: null,
  terminals: [
    {
      id: 'term-1',
      name: 'P400Plus-001',
      status: 'online',
      model: 'P400Plus',
      workstationId: 'WS01',
      ethernetIpAddress: '10.5.100.31',
      ethernetMacAddress: null,
      wifiIpAddress: null,
      firmwareVersion: '1.2.3',
      lastActivityAt: '2026-10-15T11:50:00.000Z',
    },
    {
      id: 'term-2',
      name: 'V400c-002',
      status: 'offline',
      model: 'V400c',
      workstationId: null,
      ethernetIpAddress: null,
      ethernetMacAddress: null,
      wifiIpAddress: '10.5.100.32',
      firmwareVersion: '1.2.3',
      lastActivityAt: '2026-10-10T09:00:00.000Z',
    },
  ],
  networkProfiles: [
    {
      id: 'np-0005',
      nodes: [
        {
          kind: 'ROUTER',
          name: 'MX-0005',
          serial: 'Q2XX-MOCK-0005',
          model: 'MX68',
          manufacturer: 'meraki',
          status: true,
          mac: '00:18:0a:00:00:05',
        },
        {
          kind: 'SWITCH',
          name: 'SW-0005-1',
          serial: 'Q2SW-MOCK-0005',
          model: 'MS120-24P',
          manufacturer: 'meraki',
          status: true,
          mac: '00:18:0a:00:00:15',
        },
        {
          kind: 'ACCESS_POINT',
          name: 'AP-0005-1',
          serial: 'Q2AP-MOCK-0005',
          model: 'MR36',
          manufacturer: 'meraki',
          status: false,
          mac: '00:18:0a:00:00:25',
        },
      ],
      vendorSites: [
        {
          id: 'vs-0005',
          vendorSiteNetworks: [
            {
              id: 'vsn-0005',
              reservations: [
                {
                  id: 'res-1',
                  mac: 'aa:bb:cc:00:00:01',
                  ip: '10.5.100.21',
                  type: 'printer',
                  name: 'Receipt Printer',
                  status: 'ACTIVE',
                },
                {
                  id: 'res-2',
                  mac: 'aa:bb:cc:00:00:02',
                  ip: '10.5.100.22',
                  type: 'workstation',
                  name: 'POS-1',
                  status: 'ACTIVE',
                },
                {
                  id: 'res-3',
                  mac: 'aa:bb:cc:00:00:03',
                  ip: null,
                  type: 'workstation',
                  name: 'POS-2',
                  status: 'CREATING',
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  paymentsProfiles: [],
} as unknown as SelectLocationFull

const PREVIEW_NETWORK: NetworkOverview = {
  status: {
    success: true,
    message: 'Network status fetched successfully',
    data: { 'MX-0005': 'UP', 'SW-0005-1': 'UP', 'AP-0005-1': 'UP', 'AP-0005-2': 'DOWN' },
    locationStatus: 'DEGRADED',
    tunnels: { cloudflare: { 'cf-0005': 'UP' } },
    lastPolledAt: '2026-10-15T12:00:00.000Z',
    wanInterfaces: [
      {
        interfaceName: 'wan1',
        vendor: 'meraki',
        wanType: 'primary',
        decision: 'active',
        vendorStatus: 'active',
        lastPolledAt: '2026-10-15T12:00:00.000Z',
      },
    ],
  },
  tunnel: { tunnelStatus: 'Up', deviceStatus: 'online', vpnMode: 'spoke' },
  summary: { total: 4, up: 3, down: 1, other: 0 },
}

const PREVIEW_WAN: WanSettingsData = {
  dbRecords: [
    {
      id: 'wan-1',
      type: 'PRIMARY',
      assignmentType: 'STATIC',
      assignedCidr: '203.0.113.10/30',
      assignedIp: '203.0.113.10',
      subnet: '255.255.255.252',
      gw: '203.0.113.9',
      dhcp: false,
      rangeStart: null,
      rangeEnd: null,
      status: true,
      rxSpeed: '300',
      txSpeed: '300',
      carrier: 'Comcast',
      source: 'Meraki',
      extraData: null,
    },
  ],
  vendorState: {
    vendor: 'meraki',
    interfaces: {
      wan1: { enabled: true, assignmentMode: 'static', address: '203.0.113.10/30', gateway: '203.0.113.9' },
      wan2: { enabled: false },
    },
  },
  edgeVendor: 'meraki',
  routerSerial: 'Q2XX-MOCK-0005',
  lifecycle: 'PROVISIONED',
}

const PREVIEW_PROVISIONING: ProvisioningState = {
  locationId: '00000000-0000-4000-8000-000000000005',
  lifecycle: 'CONFIGURING',
  phases: {
    ipSchema: { status: 'complete', completedAt: '2026-10-14T15:00:00.000Z' },
    register: { status: 'ready' },
    reservations: { status: 'blocked', blockedBy: ['register'] },
    dispatch: { status: 'blocked', blockedBy: ['reservations'] },
  },
  jobs: [],
  replacingSerial: null,
}

const PREVIEW_SWITCH_PORTS: SwitchPortsData = {
  switchVendor: 'MERAKI',
  ports: [
    {
      portId: '1',
      name: 'POS 1',
      type: 'access',
      vlanTag: 100,
      enabled: true,
      status: 'connected',
      clientMac: 'aa:bb:cc:00:00:02',
      clientIp: '10.5.100.22',
      clientDescription: 'POS-1',
      trafficReceived: '1.2 MB',
      trafficSent: '800 KB',
    },
    {
      portId: '2',
      name: null,
      type: 'access',
      vlanTag: 100,
      enabled: true,
      status: 'disconnected',
      clientMac: null,
      clientIp: null,
      clientDescription: null,
      trafficReceived: null,
      trafficSent: null,
    },
    {
      portId: '24',
      name: 'Uplink',
      type: 'trunk',
      vlanTag: null,
      enabled: false,
      status: 'disabled',
      clientMac: null,
      clientIp: null,
      clientDescription: null,
      trafficReceived: null,
      trafficSent: null,
    },
  ],
}

const PREVIEW_CLIENTS: LocationClients = {
  lastRefreshedAt: '2026-10-15T11:00:00.000Z',
  isStale: false,
  clients: [
    {
      mac: 'aa:bb:cc:00:00:02',
      ip: '10.5.100.22',
      vendor: 'Dell',
      vlan: '100',
      hostname: 'POS-1',
      seenIn: ['meraki'],
      lastSeenAt: '2026-10-15T11:55:00.000Z',
    },
    {
      mac: 'aa:bb:cc:00:00:09',
      ip: null,
      vendor: 'Apple',
      vlan: '18',
      hostname: null,
      seenIn: ['meraki', 'cloudflare'],
      lastSeenAt: '2026-10-15T10:30:00.000Z',
    },
  ],
}

const PREVIEW_ADMIN_ROLES: AdminRole[] = [
  { id: 'org:admin', name: 'Admin', description: 'Full access', userCount: 2 },
  { id: 'org:netops', name: 'NetOps', description: 'Network operations', userCount: 3 },
  { id: 'org:manager', name: 'Manager', description: null, userCount: 5 },
  { id: 'org:member', name: 'Member', description: 'Read-only', userCount: 12 },
]

const PREVIEW_ADMIN_USERS: AdminUser[] = [
  {
    id: 'user_1',
    name: 'Alex Rivera',
    email: 'alex.rivera@example.com',
    imageUrl: '',
    role: 'org:admin',
    joinedAt: 1_760_000_000_000,
  },
  {
    id: 'user_2',
    name: 'Sam Patel',
    email: 'sam.patel@example.com',
    imageUrl: '',
    role: 'org:netops',
    joinedAt: 1_761_000_000_000,
  },
  {
    id: 'user_3',
    name: 'Jordan Lee',
    email: 'jordan.lee@example.com',
    imageUrl: '',
    role: 'org:member',
    joinedAt: 1_762_000_000_000,
  },
]

const PREVIEW_ADMIN_PERMISSIONS: PermissionMatrixData = {
  permissions: [
    { id: 'read:locations', name: 'Read locations', groupTag: 'Locations' },
    { id: 'write:locations', name: 'Write locations', groupTag: 'Locations' },
    { id: 'read:devices', name: 'Read devices', groupTag: 'Devices' },
    { id: 'write:devices', name: 'Write devices', groupTag: 'Devices' },
    { id: 'admin:users', name: 'Manage users', groupTag: 'Admin' },
  ],
  roles: [
    {
      id: 'org:admin',
      name: 'Admin',
      description: 'Full access',
      permissions: ['read:locations', 'write:locations', 'read:devices', 'write:devices', 'admin:users'],
    },
    {
      id: 'org:manager',
      name: 'Manager',
      description: null,
      permissions: ['read:locations', 'read:devices', 'write:devices'],
    },
    { id: 'org:member', name: 'Member', description: 'Read-only', permissions: ['read:locations', 'read:devices'] },
  ],
}

const PREVIEW_ADMIN_TENANTS: TenantSummary[] = [
  {
    id: 'org_dtlr',
    name: 'DTLR',
    slug: 'dtlr',
    banner: 'dtlr',
    merakiEnabled: true,
    cfEnabled: true,
    extremeEnabled: false,
    intuneEnabled: true,
    cradlepointEnabled: true,
  },
  {
    id: 'org_spc',
    name: 'Standard Pacific',
    slug: 'spc',
    banner: 'spc',
    merakiEnabled: true,
    cfEnabled: false,
    extremeEnabled: false,
    intuneEnabled: false,
    cradlepointEnabled: null,
  },
]

const PREVIEW_ADMIN_CREDENTIALS: VendorSettings = {
  meraki: { enabled: true, orgId: '1661099', schemaStart: '10.0.0.0', apiKey: '********' },
  cloudflare: {
    enabled: true,
    accountId: '316c0ba9',
    apiEmail: 'netops@example.com',
    apiKey: '********',
    apiToken: '********',
  },
  extreme: { enabled: false },
  intune: { enabled: true, tenantId: 'tenant-mock', clientId: 'client-mock', clientSecret: '********' },
  cradlepoint: { enabled: true, apiId: 'cp-mock', apiKey: '********' },
}

const PREVIEW_ADMIN_API_TOKENS: ApiTokenView[] = [
  {
    id: '00000000-0000-4000-8000-0000000000a1',
    name: 'CI sync',
    prefixDisplay: 'dvp_a1b2c3',
    scopes: ['read:locations', 'read:devices'],
    createdBy: 'alex.rivera@example.com',
    createdAt: '2026-09-01T10:00:00.000Z',
    expiresAt: null,
    lastSeenAt: '2026-10-14T08:00:00.000Z',
    revokedAt: null,
    status: 'active',
  },
  {
    id: '00000000-0000-4000-8000-0000000000a2',
    name: 'Old script',
    prefixDisplay: 'dvp_d4e5f6',
    scopes: ['read:locations'],
    createdBy: 'sam.patel@example.com',
    createdAt: '2026-05-01T10:00:00.000Z',
    expiresAt: '2026-08-01T00:00:00.000Z',
    lastSeenAt: null,
    revokedAt: null,
    status: 'expired',
  },
]

const PREVIEW_DASHBOARD_NETSTATUS: NetstatusData = {
  cards: [
    { status: 'online', count: 16, pct: 80 },
    { status: 'degraded', count: 2, pct: 10 },
    { status: 'offline', count: 2, pct: 10 },
  ],
  wanCards: [
    { status: 'online', count: 31, pct: 78 },
    { status: 'degraded', count: 5, pct: 12 },
    { status: 'offline', count: 4, pct: 10 },
  ],
  wan1Cards: [
    { status: 'online', count: 17, pct: 85 },
    { status: 'degraded', count: 1, pct: 5 },
    { status: 'offline', count: 2, pct: 10 },
  ],
  wan2Cards: [
    { status: 'online', count: 14, pct: 70 },
    { status: 'degraded', count: 4, pct: 20 },
    { status: 'offline', count: 2, pct: 10 },
  ],
  locations: names.slice(0, 10).map(([code, name], i) => ({
    code,
    aptosStoreCode: code,
    name,
    city: 'Baltimore',
    state: 'MD',
    connectivity: i === 3 ? 'offline' : i === 6 ? 'degraded' : 'online',
    wan: i === 3 ? 'offline' : 'online',
    wan1: i === 3 ? 'offline' : 'online',
    wan2: i === 6 ? 'degraded' : 'online',
  })),
  total: 20,
  page: 1,
  pageSize: 25,
  cachedAt: '2026-10-15T12:00:00.000Z',
}

const PREVIEW_DASHBOARD_NETWORK: DashboardNetwork = {
  tunnelHealth: {
    avgUptimePct: 99.2,
    currentlyDown: 2,
    trendDelta: -0.3,
    sparkline: [99.8, 99.6, 99.9, 98.7, 99.4, 99.2],
  },
  deviceHealth: { issueCount: 6, unreachable: 2 },
  wanLinks: { locationsWithWan: 20, primary: 20, secondary: 18, tertiary: 3 },
  tunnelMix: { total: 52, cloudflare: { total: 36, up: 34 }, meraki: { total: 16, up: 15 } },
  attention: [
    { storeCode: '0007', storeName: 'Security Square', status: 'down', vendor: 'cloudflare', detail: '0/2 tunnels up' },
    {
      storeCode: '0058',
      storeName: 'Georgetown Park',
      status: 'degraded',
      vendor: 'meraki',
      detail: 'Device alerting',
    },
  ],
  range: '30d',
}

const PREVIEW_DASHBOARD_POS: DashboardPos = {
  terminalStatus: { total: 84, online: 79, offline: 5 },
  staleCheckIns: { count: 7, affectedLocations: 4 },
  unusedWorkstations: { count: 3, days: 1 },
  paymentMethods: { locationsWithAdyen: 20, active: 18, inactive: 2 },
  failingTerminals: [
    {
      terminalId: 'term-3',
      serialNumber: 'SN003',
      model: 'P400Plus',
      locationCode: '0012',
      aptosStoreCode: '0012',
      locationName: 'Towson Town Center',
      banner: 'DTLR',
      workstationId: 'WS-2',
      adyenStoreId: 'ST123',
      lastActivityAt: '2026-10-14T03:10:00.000Z',
      lastTransactionAt: '2026-10-13T22:40:00.000Z',
      reason: 'not-checking-in',
    },
  ],
  range: '1d',
}

export const PREVIEW_DATA: PreviewData = {
  locations: names.map(([code, name, active]) => ({ code, name, status: active ? 'Active' : 'Inactive' })),
  locationDetail: PREVIEW_LOCATION,
  networkOverview: PREVIEW_NETWORK,
  wanSettings: PREVIEW_WAN,
  provisioningState: PREVIEW_PROVISIONING,
  switchPorts: PREVIEW_SWITCH_PORTS,
  clients: PREVIEW_CLIENTS,
  terminalSearch: [{ id: 'term-9', name: 'P400Plus-009', serialNumber: 'SN009', model: 'P400Plus', status: 'online' }],
  printerConfig: { success: true, hasPrinter: true, printerModel: 'TM-m30', printerIp: '10.5.100.21' },
  cashDrawerConfig: { success: true, hasCashDrawer: true, drawerSize: 'small', logicalName: 'CashDrawer1' },
  adminUsers: PREVIEW_ADMIN_USERS,
  adminRoles: PREVIEW_ADMIN_ROLES,
  adminPermissions: PREVIEW_ADMIN_PERMISSIONS,
  adminTenants: PREVIEW_ADMIN_TENANTS,
  adminTenantDetail: { id: 'org_dtlr', name: 'DTLR', slug: 'dtlr', ...PREVIEW_ADMIN_CREDENTIALS },
  adminApiTokens: PREVIEW_ADMIN_API_TOKENS,
  adminCredentials: PREVIEW_ADMIN_CREDENTIALS,
  adminLocationsConfig: {
    dtlr: { excludedLocs: ['0007', '0058'], allowedLocs: [] },
    spc: { excludedLocs: [], allowedLocs: ['0201', '0202'] },
  },
  dashboardNetstatus: PREVIEW_DASHBOARD_NETSTATUS,
  dashboardNetwork: PREVIEW_DASHBOARD_NETWORK,
  dashboardPos: PREVIEW_DASHBOARD_POS,
}

const PreviewDataContext = createContext<PreviewData | null>(null)

export function PreviewDataProvider({ children, data = PREVIEW_DATA }: { children: ReactNode; data?: PreviewData }) {
  return <PreviewDataContext.Provider value={data}>{children}</PreviewDataContext.Provider>
}

/** Mock data when inside <PreviewDataProvider>, else null (real path). */
export const usePreviewData = () => useContext(PreviewDataContext)
